import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PlusIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { Gate } from '../gate'
import { Spinner } from '../spinner'
import { Modal } from '../modal'
import { CustomSelect } from '../custom-select'
import { InlineHelp } from '../inline-help'
import { useToast } from '../toast'
import { usePermissions } from 'src/hooks/use-permissions'
import { SelectOption } from 'src/services/base-model'
import { numberApi } from 'src/services/numbers/api'
import { numberQueries } from 'src/services/numbers/queries'
import {
  AvailableNumber,
  NumberStatusValue,
  numberStatusEnum,
  numberStatusLabelMap,
  OrganizationNumber,
} from 'src/services/numbers/models'
import { InfoHint } from 'src/components/info-hint'

const COUNTRY_OPTIONS: SelectOption[] = [
  { label: 'US - United States', value: 'US' },
  { label: 'CO - Colombia', value: 'CO' },
  { label: 'MX - Mexico', value: 'MX' },
  { label: 'CA - Canada', value: 'CA' },
  { label: 'GB - United Kingdom', value: 'GB' },
  { label: 'ES - Spain', value: 'ES' },
  { label: 'BR - Brazil', value: 'BR' },
  { label: 'AR - Argentina', value: 'AR' },
]

const STATUS_BADGE_CLASSES: Record<NumberStatusValue, string> = {
  [numberStatusEnum.REQUESTED]: 'bg-gray-100 text-gray-700',
  [numberStatusEnum.PROVISIONING]: 'bg-accent-50 text-accent-600',
  [numberStatusEnum.PENDING_APPROVAL]: 'bg-warning-100 text-gray-800',
  [numberStatusEnum.ACTIVE]: 'bg-green-100 text-green-700',
  [numberStatusEnum.SUSPENDED]: 'bg-error-100 text-error-700',
  [numberStatusEnum.RELEASED]: 'bg-gray-100 text-gray-500',
}

const StatusBadge = ({ status }: { status: NumberStatusValue }) => (
  <span
    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[status]}`}
    data-testid={`number-status-${status}`}
  >
    {numberStatusLabelMap[status]}
  </span>
)

const formatPrice = (number: OrganizationNumber): string => {
  if (!number.monthlyPrice) return '—'
  return `${number.priceCurrency ?? ''} ${number.monthlyPrice}/mo`.trim()
}

interface WhatsAppNumbersSectionProps {
  orgId: string
}

export const WhatsAppNumbersSection = ({ orgId }: WhatsAppNumbersSectionProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission('numbers.manage')

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [numberToCancel, setNumberToCancel] = useState<OrganizationNumber | null>(null)

  const { data: numbers, isLoading } = useQuery({
    ...numberQueries.list(orgId),
    enabled: Boolean(orgId) && canManage,
  })

  const PENDING_STATUSES: NumberStatusValue[] = [
    numberStatusEnum.REQUESTED,
    numberStatusEnum.PROVISIONING,
    numberStatusEnum.PENDING_APPROVAL,
  ]
  const hasPendingNumber = Boolean(numbers?.some((n) => PENDING_STATUSES.includes(n.status)))

  const { mutate: cancelNumber, isPending: isCancelling } = useMutation({
    mutationFn: (numberId: string) => numberApi.csc.cancel({ organization: orgId, numberId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: numberQueries.all() })
      toast({
        title: t('whatsappNumbers.subscriptionCancelledTitle'),
        description: t('whatsappNumbers.subscriptionCancelledDesc'),
        type: 'success',
      })
      setNumberToCancel(null)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || t('whatsappNumbers.cancelFailed')
      toast({ title: t('common.error'), description: message, type: 'error' })
      setNumberToCancel(null)
    },
  })

  return (
    <Gate permission="numbers.manage">
      <div
        className="rounded-lg border border-border bg-surface p-6 shadow-sm"
        data-testid="whatsapp-numbers-section"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-text-primary">
              {t('whatsappNumbers.title')}
              <InfoHint text={t('whatsappNumbers.subtitle')} className="ml-1.5" />
            </h3>
            <InlineHelp
              className="mt-2"
              text={t('help.subscriptionCost')}
              detail={t('help.subscriptionCostDetail')}
              data-testid="help-subscription-cost"
            />
          </div>
          <Button
            variant="primary"
            onClick={() => setIsAddOpen(true)}
            icon={<PlusIcon className="h-4 w-4" />}
            data-testid="add-number-button"
          >
            {t('whatsappNumbers.addNumber')}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8" data-testid="numbers-loading">
            <Spinner size="md" />
          </div>
        ) : !numbers || numbers.length === 0 ? (
          <div
            className="rounded-md border border-border bg-surface p-4 text-center text-sm text-text-placeholder"
            data-testid="numbers-empty"
          >
            {t('whatsappNumbers.empty')}
          </div>
        ) : (
          <div
            className="overflow-x-auto rounded-md border border-border"
            data-testid="numbers-table"
          >
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-surface-hover text-xs uppercase text-text-secondary">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('whatsappNumbers.number')}</th>
                  <th className="px-4 py-3 font-medium">{t('whatsappNumbers.label')}</th>
                  <th className="px-4 py-3 font-medium">{t('common.status')}</th>
                  <th className="px-4 py-3 font-medium">{t('whatsappNumbers.monthlyPrice')}</th>
                  <th className="px-4 py-3 font-medium">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {numbers.map((number) => (
                  <tr key={number.id} data-testid={`number-row-${number.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-text-primary">
                      {number.phoneNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                      {number.label}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <StatusBadge status={number.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                      {formatPrice(number)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {number.canCancel && (
                        <Button
                          variant="ghost"
                          tone="error"
                          size="sm"
                          iconOnly
                          tooltip={t('whatsappNumbers.cancelSubscription')}
                          onClick={() => setNumberToCancel(number)}
                          data-testid={`cancel-number-${number.id}`}
                        >
                          <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasPendingNumber && (
          <InlineHelp
            className="mt-4"
            text={t('help.approvalWait')}
            detail={t('help.approvalWaitDetail')}
            data-testid="help-approval-wait"
          />
        )}

        <AddNumberModal
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          organization={orgId}
        />

        {/* Cancel confirmation dialog */}
        <Modal
          isOpen={!!numberToCancel}
          onClose={() => setNumberToCancel(null)}
          title={t('whatsappNumbers.cancelSubscription')}
          size="sm"
        >
          <p className="text-sm text-text-secondary">
            {t('whatsappNumbers.cancelRentalConfirm', { number: numberToCancel?.phoneNumber })}
          </p>
          <p className="mt-2 text-xs text-text-placeholder">{t('whatsappNumbers.cancelWarning')}</p>
          <div className="mt-6 flex justify-end space-x-2">
            <Button variant="ghost" onClick={() => setNumberToCancel(null)} disabled={isCancelling}>
              {t('whatsappNumbers.keepNumber')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (numberToCancel) cancelNumber(numberToCancel.id)
              }}
              isLoading={isCancelling}
              data-testid="confirm-cancel-number"
            >
              {t('whatsappNumbers.cancelSubscription')}
            </Button>
          </div>
        </Modal>
      </div>
    </Gate>
  )
}

interface AddNumberModalProps {
  isOpen: boolean
  onClose: () => void
  organization: string
}

const AddNumberModal = ({ isOpen, onClose, organization }: AddNumberModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [country, setCountry] = useState<SelectOption[]>([COUNTRY_OPTIONS[0]])
  const [results, setResults] = useState<AvailableNumber[] | null>(null)

  const search = useMutation({
    mutationFn: () =>
      numberApi.csc.searchAvailable({ organization, country: country[0]?.value ?? 'US' }),
    onSuccess: (data) => setResults(data),
    onError: () =>
      toast({
        title: t('whatsappNumbers.searchFailedTitle'),
        description: t('whatsappNumbers.searchFailedDesc'),
        type: 'error',
      }),
  })

  const subscribe = useMutation({
    mutationFn: (phoneNumber: string) => numberApi.csc.subscribe({ organization, phoneNumber }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: numberQueries.all() })
      toast({
        title: t('whatsappNumbers.numberRequestedTitle'),
        description: t('whatsappNumbers.numberRequestedDesc'),
        type: 'success',
      })
      handleClose()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || t('whatsappNumbers.subscribeFailed')
      toast({ title: t('common.error'), description: message, type: 'error' })
    },
  })

  const handleClose = () => {
    setResults(null)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('whatsappNumbers.addModalTitle')}
      size="md"
    >
      <div className="space-y-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('whatsappNumbers.country')}
            </label>
            <CustomSelect
              options={COUNTRY_OPTIONS}
              values={country}
              onChange={(values) => setCountry(values)}
              multi={false}
              clearable={false}
              searchable
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => search.mutate()}
            isLoading={search.isPending}
            data-testid="search-numbers-button"
          >
            {t('common.search')}
          </Button>
        </div>

        {results && results.length === 0 && (
          <div
            className="rounded-md border border-border p-4 text-center text-sm text-text-placeholder"
            data-testid="available-empty"
          >
            {t('whatsappNumbers.noAvailableNumbers')}
          </div>
        )}

        {results && results.length > 0 && (
          <div className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
            {results.map((available) => (
              <div
                key={available.phoneNumber}
                className="flex items-center justify-between px-4 py-3"
                data-testid={`available-number-${available.phoneNumber}`}
              >
                <div>
                  <div className="text-sm font-medium text-text-primary">
                    {available.phoneNumber}
                  </div>
                  <div className="text-xs text-text-placeholder">
                    {available.monthlyPrice
                      ? `${available.priceUnit ?? ''} ${available.monthlyPrice}/mo`.trim()
                      : t('whatsappNumbers.priceUnavailable')}
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => subscribe.mutate(available.phoneNumber)}
                  isLoading={subscribe.isPending}
                  data-testid={`subscribe-${available.phoneNumber}`}
                >
                  {t('whatsappNumbers.subscribe')}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}
